import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H3 from '@base/H3/H3';
import Paper from '@base/Paper/Paper';
import { getQuizDetails } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { Spacings, mediaQuery } from '@styles/index';
import { STORE_FLAG } from '@cont/Store/TestSeries/FullSchedule/constants';
import pushRoute from '@utils/pushRoute';
import { getSafeGoalTestSeriesReturnPath } from '@utils/quizReturnPath';
import Actions from './Actions/Actions';
import Illustration from './Illustration/Illustration';
import Stats from './Stats/Stats';
import Tags from './Tags/Tags';
import Breadcrumbs from '../../Common/Breadcrumbs';
import OfflineStats from './Stats/OfflineStats';

const Wrapper = styled(Paper)`
  grid-column: span 24;
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_12B};
  height: fit-content;

  ${mediaQuery.uptoTablet} {
    grid-column: auto;
    width: 100%;
  }

  ${mediaQuery.uptoMobile} {
    grid-column: auto;
    width: 100%;
    padding: ${Spacings.SPACING_4B};
  }
`;

const Header = styled.div`
  grid-column: span 24;
  margin: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_9B} 0;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 670px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const TestFlexWrapper = styled(Flex)`
  ${mediaQuery.uptoMobile} {
    flex-flow: column-reverse;
  }
`;

const IllustrationWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_4B} 0;
  }
`;

const Description = () => {
  const { query } = useRouter();
  const { quizUID, source } = query;

  const isFromStore = source === STORE_FLAG;
  const goalTestSeriesReturnPath = getSafeGoalTestSeriesReturnPath(
    query.returnTo
  );

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const { is_offline: isOffline } = quizDetails;

  const getStatsAndActions = () => {
    return isOffline ? (
      <>
        <OfflineStats />
        <Actions />
      </>
    ) : (
      <>
        <Stats />
        <Actions />
      </>
    );
  };

  const onBackClick = () => {
    if (goalTestSeriesReturnPath) {
      return pushRoute(goalTestSeriesReturnPath);
    }

    const relativeLink = quizDetails?.collection?.programme?.relative_link;
    if (relativeLink.includes('test-series')) {
      if (isFromStore) {
        return pushRoute(`/store${relativeLink}/full-schedule`);
      }
      return pushRoute(`${relativeLink}/full-schedule`);
    }
    pushRoute(relativeLink);
  };

  return (
    <Wrapper>
      <Breadcrumbs onBackClick={onBackClick} useBackClick />
      <Tags />
      <TestFlexWrapper>
        <ContentWrapper>
          <Header>
            <H3 as="h1">{quizDetails.title}</H3>
          </Header>
          {getStatsAndActions()}
        </ContentWrapper>
        <IllustrationWrapper>
          <Illustration isOffline={isOffline} />
        </IllustrationWrapper>
      </TestFlexWrapper>
    </Wrapper>
  );
};

export default Description;
